import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { Loader2, Activity, Brain, Users, ShieldAlert, Gauge, Filter, HeartPulse, UserCog, Smile, Database } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShadowModeBanner } from "@/components/admin/ShadowModeBanner";

type RetrievalRow = {
  id: string;
  created_at: string;
  query_text: string;
  top1_score: number | null;
  top2_score: number | null;
  gap: number | null;
  spread: number | null;
  action: string;
};

type CalibrationRow = {
  id: string;
  created_at: string;
  task_class: string;
  sample_size: number;
  ece: number | null;
  brier: number | null;
  alert_triggered: boolean;
  window_start: string;
  window_end: string;
};

type GroupStateRow = {
  id: string;
  created_at: string;
  state: string;
  previous_state: string | null;
  confidence: number;
  transitioned: boolean;
  group_id: string | null;
};

type InhibitionRow = {
  id: string;
  created_at: string;
  action: string;
  reason: string | null;
  group_state: string | null;
  arousal: number | null;
  conflict_score: number | null;
};

type GatingRow = {
  id: string;
  created_at: string;
  decision: string;
  priority_score: number;
  reason: string | null;
  signals: Record<string, unknown> | null;
  group_id: string | null;
};

type MetacogRuntimeRow = {
  id: string;
  created_at: string;
  decision: string;
  task_class: string | null;
  pre_response_confidence: number;
  applied: boolean;
  reason: string | null;
};

type InteroceptiveRow = {
  id: string;
  created_at: string;
  arousal: number;
  threat_rate: number;
  error_rate: number;
  budget_pressure: number;
  temp_eff: number;
  base_temp: number;
};

const GATING_BADGE: Record<string, string> = {
  process: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  defer: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  drop: "bg-destructive/10 text-destructive border-destructive/20",
};

type TomRow = {
  id: string;
  updated_at: string;
  member_jid: string;
  display_name: string | null;
  formality: number;
  verbosity: number;
  sentiment_baseline: number;
  observation_count: number;
  last_intent: string | null;
};

type AffectRow = {
  id: string;
  created_at: string;
  group_id: string | null;
  valence: number;
  arousal: number;
  dominance: number;
};

type ConsolidationRow = {
  id: string;
  created_at: string;
  candidates_seen: number;
  reinforced: number;
  decayed: number;
  merged: number;
  quarantined: number;
  duration_ms: number | null;
};

type PanelProps = {
  orgId: string | null;
};


const STATE_BADGE: Record<string, string> = {
  cooperative: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  noisy: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  provocative: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  degraded: "bg-destructive/10 text-destructive border-destructive/20",
  serious_in_chaos: "bg-red-700/10 text-red-700 border-red-700/20",
  high_sensitivity: "bg-violet-500/10 text-violet-600 border-violet-500/20",
};

const ACTION_BADGE: Record<string, string> = {
  send: "bg-emerald-500/10 text-emerald-600",
  shorten: "bg-sky-500/10 text-sky-600",
  rephrase: "bg-violet-500/10 text-violet-600",
  replace_with_neutral: "bg-amber-500/10 text-amber-600",
  ask_clarification: "bg-amber-500/10 text-amber-600",
  block: "bg-destructive/10 text-destructive",
  log_and_silence: "bg-muted text-muted-foreground",
  escalate_to_human: "bg-orange-500/10 text-orange-600",
};

function fmtPct(n: number | null | undefined, digits = 1) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}
function fmtNum(n: number | null | undefined, digits = 3) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number(n).toFixed(digits);
}
function fmtDate(s: string) {
  return new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

export default function Operacao() {
  const { data: orgId } = useCurrentOrgId();
  const [tab, setTab] = useState("pattern-sep");

  return (
    <div className="container max-w-7xl py-8 space-y-6">
      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1.5 border-primary/40 bg-primary/5 text-primary">
            <Brain className="h-3.5 w-3.5" />
            Cortex OS · Cockpit Avançado
          </Badge>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-muted-foreground">
            modo avançado
          </Badge>
        </div>
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight font-headline">Cockpit Cortex OS</h1>
          <p className="text-muted-foreground max-w-3xl">
            Telemetria operacional dos 9 eventos canônicos do Cortex OS — Pattern Separation, Calibração de
            Confiança, Estado de Grupo, Response Inhibitor, Input Gating, Interoceptive, ToM, Affect e Consolidation.
          </p>
          {!orgId ? <p className="text-sm text-muted-foreground">Nenhuma organização ativa encontrada para carregar a telemetria.</p> : null}
        </div>
      </header>

      <ShadowModeBanner context="Métricas como Calibração, ToM e Metacog dependem de flags Fase 3." compact />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 w-full max-w-5xl">
          <TabsTrigger value="pattern-sep" className="gap-2"><Brain className="h-4 w-4" /> Pattern Sep</TabsTrigger>
          <TabsTrigger value="calibration" className="gap-2"><Gauge className="h-4 w-4" /> Calibração</TabsTrigger>
          <TabsTrigger value="group-state" className="gap-2"><Users className="h-4 w-4" /> Estado</TabsTrigger>
          <TabsTrigger value="inhibitor" className="gap-2"><ShieldAlert className="h-4 w-4" /> Inhibitor</TabsTrigger>
          <TabsTrigger value="gating" className="gap-2"><Filter className="h-4 w-4" /> Gating</TabsTrigger>
          <TabsTrigger value="metacog" className="gap-2"><Activity className="h-4 w-4" /> Metacog</TabsTrigger>
          <TabsTrigger value="interoceptive" className="gap-2"><HeartPulse className="h-4 w-4" /> Interoceptive</TabsTrigger>
          <TabsTrigger value="tom" className="gap-2"><UserCog className="h-4 w-4" /> ToM</TabsTrigger>
          <TabsTrigger value="affect" className="gap-2"><Smile className="h-4 w-4" /> Affect</TabsTrigger>
          <TabsTrigger value="consolidation" className="gap-2"><Database className="h-4 w-4" /> Consolidation</TabsTrigger>
        </TabsList>

        <TabsContent value="pattern-sep"><PatternSepPanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="calibration"><CalibrationPanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="group-state"><GroupStatePanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="inhibitor"><InhibitorPanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="gating"><GatingPanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="metacog"><MetacogPanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="interoceptive"><InteroceptivePanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="tom"><TomPanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="affect"><AffectPanel orgId={orgId ?? null} /></TabsContent>
        <TabsContent value="consolidation"><ConsolidationPanel orgId={orgId ?? null} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ── Panels ───────────────────────────────────────────────────────────

function PatternSepPanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<RetrievalRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("memory_retrieval_trace")
      .select("id, created_at, query_text, top1_score, top2_score, gap, spread, action")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as RetrievalRow[]) ?? []));
  }, [orgId]);

  const dist = useMemo(() => {
    const acc: Record<string, number> = { proceed: 0, ask_user: 0, escalate_with_context: 0 };
    rows?.forEach((r) => { acc[r.action] = (acc[r.action] ?? 0) + 1; });
    const total = rows?.length || 0;
    return { acc, total };
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pattern Separation</CardTitle>
        <CardDescription>
          Decisões recentes de retrieval — gap (top1−top2) e spread. Thresholds oficiais: gap=0.05, spread=0.30.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {(["proceed", "ask_user", "escalate_with_context"] as const).map((a) => (
            <div key={a} className="rounded-lg border p-3">
              <div className="text-xs uppercase text-muted-foreground tracking-wider">{a}</div>
              <div className="text-2xl font-semibold">{dist.acc[a] ?? 0}</div>
              <div className="text-xs text-muted-foreground">
                {dist.total ? fmtPct((dist.acc[a] ?? 0) / dist.total) : "—"}
              </div>
            </div>
          ))}
        </div>

        {rows === null ? (
          <Loader />
        ) : rows.length === 0 ? (
          <Empty msg="Nenhum traço de retrieval registrado ainda." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Top1</TableHead>
                  <TableHead className="text-right">Gap</TableHead>
                  <TableHead className="text-right">Spread</TableHead>
                  <TableHead>Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="max-w-[280px] truncate" title={r.query_text}>{r.query_text}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.top1_score)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.gap)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.spread)}</TableCell>
                    <TableCell><Badge variant="outline">{r.action}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CalibrationPanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<CalibrationRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("confidence_calibration_runs")
      .select("id, created_at, task_class, sample_size, ece, brier, alert_triggered, window_start, window_end")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as CalibrationRow[]) ?? []));
  }, [orgId]);

  const latestByTask = useMemo(() => {
    if (!rows) return {} as Record<string, CalibrationRow>;
    const acc: Record<string, CalibrationRow> = {};
    for (const r of rows) if (!acc[r.task_class]) acc[r.task_class] = r;
    return acc;
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Calibração de Confiança (ECE + Brier)</CardTitle>
        <CardDescription>
          Gates Doc 4 — ECE alerta &gt; 0.20, gate &lt; 0.15, Brier &lt; 0.20. Cron semanal popula esta tabela.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["intent_classify", "memory_search", "threat_detect", "group_state"].map((tc) => {
            const r = latestByTask[tc];
            const ece = r?.ece ?? null;
            const tone = ece === null ? "muted" : ece > 0.20 ? "destructive" : ece > 0.15 ? "amber" : "emerald";
            return (
              <div key={tc} className="rounded-lg border p-3">
                <div className="text-xs uppercase text-muted-foreground tracking-wider">{tc}</div>
                <div className="text-2xl font-semibold font-mono">{fmtNum(ece, 3)}</div>
                <div className="text-xs flex items-center gap-1">
                  <span className="text-muted-foreground">ECE</span>
                  {r ? (
                    <Badge variant="outline" className={
                      tone === "destructive" ? "border-destructive text-destructive" :
                      tone === "amber" ? "border-amber-500 text-amber-600" :
                      tone === "emerald" ? "border-emerald-500 text-emerald-600" : ""
                    }>
                      {tone === "destructive" ? "alerta" : tone === "amber" ? "atenção" : tone === "emerald" ? "ok" : "—"}
                    </Badge>
                  ) : <span className="text-muted-foreground text-[10px]">sem dados</span>}
                </div>
                {r && <div className="text-xs text-muted-foreground mt-1">Brier {fmtNum(r.brier, 3)} · n={r.sample_size}</div>}
              </div>
            );
          })}
        </div>

        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Nenhuma execução de calibração ainda — aguarde o cron semanal ou dispare manualmente." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Task</TableHead>
                  <TableHead className="text-right">Amostras</TableHead>
                  <TableHead className="text-right">ECE</TableHead>
                  <TableHead className="text-right">Brier</TableHead>
                  <TableHead>Alerta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell><Badge variant="outline">{r.task_class}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.sample_size}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.ece, 4)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.brier, 4)}</TableCell>
                    <TableCell>{r.alert_triggered ? <Badge variant="destructive">sim</Badge> : <Badge variant="outline">não</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GroupStatePanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<GroupStateRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("group_state_classifications")
      .select("id, created_at, state, previous_state, confidence, transitioned, group_id")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as GroupStateRow[]) ?? []));
  }, [orgId]);

  const dist = useMemo(() => {
    const acc: Record<string, number> = {};
    rows?.forEach((r) => { acc[r.state] = (acc[r.state] ?? 0) + 1; });
    const total = rows?.length || 0;
    return { acc, total };
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Estado de Grupo</CardTitle>
        <CardDescription>
          6 estados oficiais com histerese: cooperative, noisy, provocative, degraded, serious_in_chaos, high_sensitivity.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {Object.keys(STATE_BADGE).map((s) => (
            <div key={s} className="rounded-lg border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s}</div>
              <div className="text-xl font-semibold">{dist.acc[s] ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">{dist.total ? fmtPct((dist.acc[s] ?? 0) / dist.total) : "—"}</div>
            </div>
          ))}
        </div>

        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Nenhuma classificação de grupo registrada ainda." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Anterior</TableHead>
                  <TableHead className="text-right">Confiança</TableHead>
                  <TableHead>Transição</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATE_BADGE[r.state] ?? ""}>{r.state}</Badge>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-muted-foreground">{r.previous_state ?? "—"}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtPct(r.confidence)}</TableCell>
                    <TableCell>{r.transitioned ? <Badge>sim</Badge> : <Badge variant="outline">não</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InhibitorPanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<InhibitionRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("inhibition_decisions")
      .select("id, created_at, action, reason, group_state, arousal, conflict_score")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as InhibitionRow[]) ?? []));
  }, [orgId]);

  const dist = useMemo(() => {
    const acc: Record<string, number> = {};
    rows?.forEach((r) => { acc[r.action] = (acc[r.action] ?? 0) + 1; });
    const total = rows?.length || 0;
    return { acc, total };
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Response Inhibitor</CardTitle>
        <CardDescription>
          8 ações oficiais. Gates: false_inhibition &lt; 3%, log_and_silence em degraded ≥ 80%.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.keys(ACTION_BADGE).map((a) => (
            <div key={a} className="rounded-lg border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{a}</div>
              <div className="text-xl font-semibold">{dist.acc[a] ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">{dist.total ? fmtPct((dist.acc[a] ?? 0) / dist.total) : "—"}</div>
            </div>
          ))}
        </div>

        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Nenhuma decisão do Inhibitor registrada ainda." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Arousal</TableHead>
                  <TableHead className="text-right">Conflict</TableHead>
                  <TableHead>Razão</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell><Badge variant="outline" className={ACTION_BADGE[r.action] ?? ""}>{r.action}</Badge></TableCell>
                    <TableCell>{r.group_state ? <Badge variant="outline" className={STATE_BADGE[r.group_state] ?? ""}>{r.group_state}</Badge> : "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.arousal, 2)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.conflict_score, 2)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate" title={r.reason ?? ""}>{r.reason ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GatingPanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<GatingRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("input_gating_decisions" as never)
      .select("id, created_at, decision, priority_score, reason, signals, group_id")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as GatingRow[]) ?? []));
  }, [orgId]);

  const dist = useMemo(() => {
    const acc: Record<string, number> = { process: 0, defer: 0, drop: 0 };
    rows?.forEach((r) => { acc[r.decision] = (acc[r.decision] ?? 0) + 1; });
    const total = rows?.length || 0;
    return { acc, total };
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Input Gating (S3.5)</CardTitle>
        <CardDescription>
          Admissão prioritária de mensagens. Thresholds: process ≥ 0.45, defer ≥ 0.20, demais → drop. Em <code>degraded</code> aumenta drop floor para 0.30.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {(["process", "defer", "drop"] as const).map((a) => (
            <div key={a} className="rounded-lg border p-3">
              <div className="text-xs uppercase text-muted-foreground tracking-wider">{a}</div>
              <div className="text-2xl font-semibold">{dist.acc[a] ?? 0}</div>
              <div className="text-xs text-muted-foreground">{dist.total ? fmtPct((dist.acc[a] ?? 0) / dist.total) : "—"}</div>
            </div>
          ))}
        </div>

        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Nenhuma decisão de gating ainda — flag cortex.input_gating começa OFF." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead>Decisão</TableHead>
                  <TableHead className="text-right">Prioridade</TableHead>
                  <TableHead>Razão</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell><Badge variant="outline" className={GATING_BADGE[r.decision] ?? ""}>{r.decision}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.priority_score, 3)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate" title={r.reason ?? ""}>{r.reason ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetacogPanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<MetacogRuntimeRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("metacog_runtime_decisions")
      .select("id, created_at, decision, task_class, pre_response_confidence, applied, reason")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as MetacogRuntimeRow[]) ?? []));
  }, [orgId]);

  const dist = useMemo(() => {
    const acc: Record<string, number> = { execute: 0, clarify: 0, abstain: 0 };
    rows?.forEach((row) => { acc[row.decision] = (acc[row.decision] ?? 0) + 1; });
    const total = rows?.length || 0;
    return { acc, total };
  }, [rows]);
  const appliedRate = useMemo(() => {
    if (!rows?.length) return null;
    return rows.filter((row) => row.applied).length / rows.length;
  }, [rows]);
  const avgConfidence = useMemo(() => {
    if (!rows?.length) return null;
    return rows.reduce((sum, row) => sum + Number(row.pre_response_confidence ?? 0), 0) / rows.length;
  }, [rows]);
  const byTaskClass = useMemo(() => {
    if (!rows?.length) return [] as Array<{ task: string; total: number; clarify: number; abstain: number }>;
    const map = new Map<string, { total: number; clarify: number; abstain: number }>();
    rows.forEach((row) => {
      const key = row.task_class ?? "unknown";
      const current = map.get(key) ?? { total: 0, clarify: 0, abstain: 0 };
      current.total += 1;
      if (row.decision === "clarify") current.clarify += 1;
      if (row.decision === "abstain") current.abstain += 1;
      map.set(key, current);
    });
    return Array.from(map.entries())
      .map(([task, stats]) => ({ task, ...stats }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Metacognition Runtime</CardTitle>
        <CardDescription>
          Autoavaliação do fluxo conversacional principal antes da resposta final: execute, clarify ou abstain.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {(["execute", "clarify", "abstain"] as const).map((decision) => (
            <div key={decision} className="rounded-lg border p-3">
              <div className="text-xs uppercase text-muted-foreground tracking-wider">{decision}</div>
              <div className="text-2xl font-semibold">{dist.acc[decision] ?? 0}</div>
              <div className="text-xs text-muted-foreground">{dist.total ? fmtPct((dist.acc[decision] ?? 0) / dist.total) : "—"}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">apply rate</div>
            <div className="text-2xl font-semibold">{appliedRate == null ? "—" : fmtPct(appliedRate)}</div>
            <div className="text-xs text-muted-foreground">decisões realmente aplicadas</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">avg confidence</div>
            <div className="text-2xl font-semibold font-mono">{fmtNum(avgConfidence, 3)}</div>
            <div className="text-xs text-muted-foreground">média das últimas 50 decisões</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">clarify+abstain</div>
            <div className="text-2xl font-semibold">{dist.total ? fmtPct(((dist.acc.clarify ?? 0) + (dist.acc.abstain ?? 0)) / dist.total) : "—"}</div>
            <div className="text-xs text-muted-foreground">pressão de contenção no runtime</div>
          </div>
        </div>

        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Nenhuma decisão de metacog registrada ainda." />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quando</TableHead>
                      <TableHead>Decisão</TableHead>
                      <TableHead>Task</TableHead>
                      <TableHead className="text-right">Confiança</TableHead>
                      <TableHead>Aplicado</TableHead>
                      <TableHead>Razão</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(row.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant={row.decision === "abstain" ? "destructive" : row.decision === "clarify" ? "secondary" : "outline"}>
                            {row.decision}
                          </Badge>
                        </TableCell>
                        <TableCell>{row.task_class ? <Badge variant="outline">{row.task_class}</Badge> : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmtNum(row.pre_response_confidence, 4)}</TableCell>
                        <TableCell>{row.applied ? <Badge>sim</Badge> : <Badge variant="outline">não</Badge>}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate" title={row.reason ?? ""}>{row.reason ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="rounded-md border p-4 space-y-3">
                <div>
                  <div className="text-sm font-medium">Hotspots por task</div>
                  <div className="text-xs text-muted-foreground">Onde o runtime está pedindo mais contenção.</div>
                </div>
                <div className="space-y-2">
                  {byTaskClass.map((item) => (
                    <div key={item.task} className="rounded border px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="outline">{item.task}</Badge>
                        <span className="text-xs text-muted-foreground">{item.total} turns</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <Badge variant="secondary">clarify {item.clarify}</Badge>
                        <Badge variant={item.abstain > 0 ? "destructive" : "outline"}>abstain {item.abstain}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InteroceptivePanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<InteroceptiveRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("interoceptive_states" as never)
      .select("id, created_at, arousal, threat_rate, error_rate, budget_pressure, temp_eff, base_temp")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as InteroceptiveRow[]) ?? []));
  }, [orgId]);

  const latest = rows?.[0];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Estado Interoceptivo (S3.6)</CardTitle>
        <CardDescription>
          arousal = 0.4·threat + 0.3·error + 0.3·budget — temp_eff = base · (1 − 0.5·arousal). Cron 5–15 min.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">arousal</div>
            <div className="text-2xl font-semibold font-mono">{fmtNum(latest?.arousal, 3)}</div>
            <div className="text-[10px] text-muted-foreground">0 = calmo · 1 = alerta máx</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">temp_eff</div>
            <div className="text-2xl font-semibold font-mono">{fmtNum(latest?.temp_eff, 3)}</div>
            <div className="text-[10px] text-muted-foreground">base {fmtNum(latest?.base_temp, 2)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">threat</div>
            <div className="text-xl font-semibold font-mono">{fmtPct(latest?.threat_rate)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">error</div>
            <div className="text-xl font-semibold font-mono">{fmtPct(latest?.error_rate)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">budget</div>
            <div className="text-xl font-semibold font-mono">{fmtPct(latest?.budget_pressure)}</div>
          </div>
        </div>

        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Sem snapshots interoceptivos ainda — flag cortex.interoceptive começa OFF." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead className="text-right">Arousal</TableHead>
                  <TableHead className="text-right">Threat</TableHead>
                  <TableHead className="text-right">Error</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">temp_eff</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.arousal, 3)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtPct(r.threat_rate)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtPct(r.error_rate)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtPct(r.budget_pressure)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.temp_eff, 3)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TomPanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<TomRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("tom_models" as never)
      .select("id, updated_at, member_jid, display_name, formality, verbosity, sentiment_baseline, observation_count, last_intent")
      .eq("org_id", orgId)
      .order("updated_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as TomRow[]) ?? []));
  }, [orgId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Theory of Mind (S4.1)</CardTitle>
        <CardDescription>
          Modelo por participante atualizado por EWMA (α=0.2) — formality, verbosity e sentimento baseline. Flag <code>cortex.tom</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Nenhum modelo ToM ainda — flag começa OFF." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Atualizado</TableHead>
                  <TableHead>Membro</TableHead>
                  <TableHead className="text-right">Formality</TableHead>
                  <TableHead className="text-right">Verbosity</TableHead>
                  <TableHead className="text-right">Sentimento</TableHead>
                  <TableHead className="text-right">Obs.</TableHead>
                  <TableHead>Último intent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.updated_at)}</TableCell>
                    <TableCell className="text-xs">{r.display_name ?? r.member_jid}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.formality, 2)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.verbosity, 2)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.sentiment_baseline, 2)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.observation_count}</TableCell>
                    <TableCell>{r.last_intent ? <Badge variant="outline">{r.last_intent}</Badge> : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AffectPanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<AffectRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("affect_states" as never)
      .select("id, created_at, group_id, valence, arousal, dominance")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as AffectRow[]) ?? []));
  }, [orgId]);

  const latest = rows?.[0];
  const moodLabel = (v?: number, a?: number) => {
    if (v === undefined || a === undefined) return "—";
    if (v >= 0.3) return a >= 0.5 ? "entusiasmado" : "satisfeito";
    if (v <= -0.3) return a >= 0.5 ? "tenso" : "desanimado";
    return a >= 0.5 ? "agitado" : "calmo";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Estado Afetivo do Grupo (S4.2)</CardTitle>
        <CardDescription>
          VAD agregado em janela de 30 min — valence ∈ [-1,1], arousal/dominance ∈ [0,1]. Flag <code>cortex.affect</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">mood</div>
            <div className="text-2xl font-semibold">{moodLabel(latest?.valence, latest?.arousal)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">valence</div>
            <div className="text-2xl font-semibold font-mono">{fmtNum(latest?.valence, 3)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">arousal</div>
            <div className="text-2xl font-semibold font-mono">{fmtNum(latest?.arousal, 3)}</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wider">dominance</div>
            <div className="text-2xl font-semibold font-mono">{fmtNum(latest?.dominance, 3)}</div>
          </div>
        </div>

        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Sem snapshots de afeto ainda — rode affect-aggregate ou aguarde cron." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead className="text-right">Valence</TableHead>
                  <TableHead className="text-right">Arousal</TableHead>
                  <TableHead className="text-right">Dominance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.valence, 3)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.arousal, 3)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtNum(r.dominance, 3)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConsolidationPanel({ orgId }: PanelProps) {
  const [rows, setRows] = useState<ConsolidationRow[] | null>(null);
  useEffect(() => {
    if (!orgId) {
      setRows([]);
      return;
    }
    supabase.from("memory_consolidation_runs" as never)
      .select("id, created_at, candidates_seen, reinforced, decayed, merged, quarantined, duration_ms")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => setRows((data as ConsolidationRow[]) ?? []));
  }, [orgId]);

  const totals = useMemo(() => {
    const acc = { reinforced: 0, decayed: 0, merged: 0, quarantined: 0 };
    rows?.forEach((r) => { acc.reinforced += r.reinforced; acc.decayed += r.decayed; acc.merged += r.merged; acc.quarantined += r.quarantined; });
    return acc;
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Memory Consolidation v2 (S4.3)</CardTitle>
        <CardDescription>
          Reforço (24h, +5% conf), decay (7d default / 30d episodic), merge (dedup exact), quarentena (&lt;0.05). Flag <code>cortex.consolidation_v2</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg border p-3"><div className="text-xs uppercase text-muted-foreground tracking-wider">reinforced</div><div className="text-2xl font-semibold">{totals.reinforced}</div></div>
          <div className="rounded-lg border p-3"><div className="text-xs uppercase text-muted-foreground tracking-wider">decayed</div><div className="text-2xl font-semibold">{totals.decayed}</div></div>
          <div className="rounded-lg border p-3"><div className="text-xs uppercase text-muted-foreground tracking-wider">merged</div><div className="text-2xl font-semibold">{totals.merged}</div></div>
          <div className="rounded-lg border p-3"><div className="text-xs uppercase text-muted-foreground tracking-wider">quarantined</div><div className="text-2xl font-semibold">{totals.quarantined}</div></div>
        </div>

        {rows === null ? <Loader /> : rows.length === 0 ? (
          <Empty msg="Nenhuma execução do consolidador v2 ainda." />
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quando</TableHead>
                  <TableHead className="text-right">Candidatos</TableHead>
                  <TableHead className="text-right">Reforçadas</TableHead>
                  <TableHead className="text-right">Decaídas</TableHead>
                  <TableHead className="text-right">Fundidas</TableHead>
                  <TableHead className="text-right">Quarentena</TableHead>
                  <TableHead className="text-right">Duração</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.candidates_seen}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.reinforced}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.decayed}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.merged}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.quarantined}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.duration_ms ?? "—"} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function Loader() {
  return (
    <div className="flex items-center justify-center py-12 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Carregando…
    </div>
  );
}
function Empty({ msg }: { msg: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
      <Activity className="h-5 w-5" /> {msg}
    </div>
  );
}
